import { useState } from 'react';

import type { MlChangePersonInstanceSchema } from '@iconik-io/iconik-api/src/services/ml';

import { errorMessage } from '../../../../ducks/messages/thunks';
import {
  addPerson,
  replacePersonBasedOnVersion,
} from '../../../../ducks/panels/asset_face_recognition/thunks';
import { useSegmentTrackEvent } from '../../../../hooks/use_segment_track_event';
import { useAppDispatch } from '../../../../store/react_redux_typed';
import type { PersonSelectProps } from '../types';

type HandleSaveParameters = {
  saveAsNewPerson: boolean;
  analyticsPlace: 'player' | 'panel' | 'management';
};

export type UsePersonSelectProps = Omit<PersonSelectProps, 'rowType'>;

export const usePersonSelect = ({
  onError,
  onSuccess,
  onDeactivate,
  onSetShowPersonSelect,
  segments,
  person,
  actionType,
}: UsePersonSelectProps) => {
  const dispatch = useAppDispatch();
  const [personBody, setPersonBody] = useState<MlChangePersonInstanceSchema>({
    new_person_name: undefined,
    new_person_id: undefined,
  });
  const { trackSegmentEvent } = useSegmentTrackEvent();
  const [status, setStatus] = useState<'initial' | 'pending'>('initial');

  const {
    id: personId,
    asset_id: assetId,
    version_id: versionId,
    person_version_face_id: personVersionFaceId,
  } = person ?? {};

  const handleSave = async ({
    saveAsNewPerson,
    analyticsPlace,
  }: HandleSaveParameters) => {
    if (!personId || !assetId || !versionId) {
      return;
    }
    if (!personBody.new_person_id && !personBody.new_person_name?.trim()) {
      dispatch(
        errorMessage({ text: 'Please select a person or type a new name' }),
      );
      return;
    }

    setStatus('pending');
    onDeactivate?.();

    if (actionType === 'add') {
      // WORKFLOW A: ADD PERSON (Update name and confirm all instances)
      if (personBody.new_person_name) {
        return dispatch(
          addPerson(
            personId,
            {
              name: personBody.new_person_name,
              all_instances_except_unconfirmed: true,
              version_ids: [versionId],
            },
            saveAsNewPerson,
          ),
        )
          .then(() => {
            onSuccess?.();
            onSetShowPersonSelect?.(false);
            trackSegmentEvent('Added Person', {
              Place: analyticsPlace,
              Profile: 'new',
            });
          })
          .catch(() => {
            onError?.();
            setStatus('initial');
          });
      }
    } else {
      // WORKFLOW B: CHANGE PERSON (Move the instance to a new/different person)
      return dispatch(
        replacePersonBasedOnVersion(
          personId,
          assetId,
          versionId,
          personBody,
          segments,
          personVersionFaceId,
        ),
      )
        .then(() => {
          onSuccess?.();
          onSetShowPersonSelect?.(false);
          trackSegmentEvent('Changed Person', {
            Place: analyticsPlace,
            Profile: saveAsNewPerson ? 'new' : 'existing',
          });
        })
        .catch(() => {
          onError?.();
          setStatus('initial');
        });
    }
  };

  return {
    personBody,
    setPersonBody,
    status,
    setStatus,
    handleSave,
  };
};
